import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { ArrowLeftIcon, DocumentIcon, IdBackIcon } from '@persona-id/design-tokens/icons';
import styled from 'styled-components';

import { View } from 'dashboard/components/PlanUpgradeFlow';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { AvailableOrganizationPlan } from 'dashboard/models/organization-plan';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';

interface PlanUpgradeInstructionsProps {
  newOrganizationPlan: AvailableOrganizationPlan | null;
  handleNavToNextView: (
    currentView: View,
    newOrganizationPlan: AvailableOrganizationPlan | null,
  ) => void;
  handleNavToPreviousView: (currentView: View) => void;
  onClose: () => void;
}

const PlanUpgradeInstructions = ({
  newOrganizationPlan,
  handleNavToNextView,
  handleNavToPreviousView,
  onClose,
}: PlanUpgradeInstructionsProps) => {
  return (
    <StyledModalContainer>
      <ModalHeader
        prefixIcon={<ArrowLeftIcon size={20} />}
        onPrefixIconClick={() => handleNavToPreviousView(View.PlanUpgradeInstructions)}
        onClose={onClose}
      >
        Upgrade plan
      </ModalHeader>
      <ModalBody>
        <Paragraph size='lg' color='primary'>
          In order to upgrade your plan, Persona will verify your business to ensure responsible use
          of the platform and end user data. To do this, we’ll need you to answer a few questions
          and prepare the following:
        </Paragraph>
        <Spacer height={4} />
        <ListWrapper>
          <ListRow>
            <IdBackIcon size={20} />
            <div>
              <ItemTitle>Payment method</ItemTitle>
              <ItemDescription>
                We will collect a payment method if you have not already provided one. You will not
                be charged right away.
              </ItemDescription>
            </div>
          </ListRow>
          <ListRow>
            <IdBackIcon size={20} />
            <div>
              <ItemTitle>Business Tax Identification Number (TIN)</ItemTitle>
              <ItemDescription>
                For non-US businesses, the equivalent identification number works great.
              </ItemDescription>
            </div>
          </ListRow>
          <ListRow>
            <DocumentIcon size={20} />
            <div>
              <ItemTitle>Business Creation Documents</ItemTitle>
              <ItemDescription>
                These are often called Articles of Incorporation or Establishment and are filed with
                a government entity.
              </ItemDescription>
            </div>
          </ListRow>
        </ListWrapper>
        <Spacer height={4} />
        <Footer>
          <Button
            width='auto'
            onClick={() => {
              handleNavToNextView(View.PlanUpgradeInstructions, newOrganizationPlan);
            }}
          >
            Continue
          </Button>
        </Footer>
      </ModalBody>
    </StyledModalContainer>
  );
};

const StyledModalContainer = styled(ModalContainer)`
  min-width: 700px;
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${Spacing[4]};
  margin: 0px ${Spacing[4]};
`;

const ListRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[4]};
`;

const ItemTitle = styled.div`
  ${Typography.TextMediumBold};
`;

const ItemDescription = styled.div`
  ${Typography.TextMedium};
  color: ${Color.TextSecondary};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export default PlanUpgradeInstructions;
